import React, { useMemo, useState } from 'react';
import {
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useEmployeeOptions } from './shared/useEmployeeOptions';
import { HRMS_GENERIC_STATUS_COLOR, hrmsStatusColor } from './shared/hrmsStatus';

const { Text } = Typography;

const OvertimePage: React.FC = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>();
  const [toilEmployeeId, setToilEmployeeId] = useState<string>();
  const [consumeOpen, setConsumeOpen] = useState(false);
  const [consumeForm] = Form.useForm();
  const [consuming, setConsuming] = useState(false);
  const { options: empOptions, empLabel } = useEmployeeOptions();

  const { data: rows = [], isLoading } = useQuery({
    queryKey: hrmsKeys.otRequests(),
    queryFn: () => hrmsService.listOtRequests(),
  });

  const { data: toilRows = [], isLoading: toilLoading } = useQuery({
    queryKey: hrmsKeys.toilBank(toilEmployeeId),
    queryFn: () => hrmsService.listToilBank(toilEmployeeId ? { employee_id: toilEmployeeId } : undefined),
  });

  const filtered = useMemo(
    () => (statusFilter ? rows.filter((r: any) => r.status === statusFilter) : rows),
    [rows, statusFilter]
  );

  const stats = useMemo(() => {
    const pending = rows.filter((r: any) => r.status === 'Draft' || r.status === 'Pending').length;
    const approved = rows.filter((r: any) => r.status === 'Approved').length;
    const hours = rows.reduce((s: number, r: any) => s + Number(r.requested_hours || 0), 0);
    return { total: rows.length, pending, approved, hours };
  }, [rows]);

  const toilBalance = useMemo(() => {
    if (!toilRows.length) return 0;
    const last = toilRows[toilRows.length - 1];
    if (last.balance_after != null) return Number(last.balance_after);
    return toilRows.reduce((s: number, r: any) => s + Number(r.hours || 0), 0);
  }, [toilRows]);

  const columns: ColumnsType<any> = [
    {
      title: 'OT #',
      dataIndex: 'ot_number',
      width: 130,
      fixed: 'left',
      render: (v: string, r) => (
        <a onClick={() => navigate(`/hr/overtime/${r.id}`)}>{v || 'Open'}</a>
      ),
    },
    { title: 'Employee', dataIndex: 'employee_id', ellipsis: true, render: (id: string) => empLabel(id) },
    {
      title: 'Date',
      dataIndex: 'ot_date',
      width: 120,
      render: (d: string) => (d ? dayjs(d).format('DD-MMM-YYYY') : '—'),
    },
    { title: 'Requested', dataIndex: 'requested_hours', width: 100 },
    {
      title: 'Kind',
      dataIndex: 'ot_kind',
      width: 100,
      render: (v: string) => v || 'Normal',
    },
    { title: 'Approved', dataIndex: 'approved_hours', width: 100 },
    { title: 'Disposition', dataIndex: 'excess_disposition', width: 110 },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: (s: string) => <Tag color={hrmsStatusColor(s)}>{s}</Tag>,
    },
    {
      title: 'Actions',
      key: 'a',
      fixed: 'right',
      width: 90,
      render: (_, r) => (
        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/hr/overtime/${r.id}`)}>
          View
        </Button>
      ),
    },
  ];

  const toilColumns: ColumnsType<any> = [
    {
      title: 'Employee',
      dataIndex: 'employee_id',
      ellipsis: true,
      render: (id: string) => empLabel(id),
    },
    {
      title: 'Date',
      dataIndex: 'txn_date',
      width: 120,
      render: (d: string) => (d ? dayjs(d).format('DD-MMM-YYYY') : '—'),
    },
    {
      title: 'Hours',
      dataIndex: 'hours',
      width: 90,
      render: (h: number) => (
        <Text type={Number(h) < 0 ? 'danger' : undefined}>{Number(h).toFixed(2)}</Text>
      ),
    },
    {
      title: 'Balance after',
      dataIndex: 'balance_after',
      width: 110,
      render: (v: number) => (v != null ? Number(v).toFixed(2) : '—'),
    },
    { title: 'Source', dataIndex: 'source', width: 120 },
    { title: 'Notes', dataIndex: 'notes', ellipsis: true, render: (v) => v || '—' },
  ];

  const consume = async () => {
    try {
      const v = await consumeForm.validateFields();
      setConsuming(true);
      await hrmsService.consumeToil({
        employee_id: v.employee_id,
        hours: v.hours,
        consume_date: v.consume_date.format('YYYY-MM-DD'),
        notes: v.notes,
      });
      message.success('TOIL hours consumed');
      setConsumeOpen(false);
      consumeForm.resetFields();
      queryClient.invalidateQueries({ queryKey: hrmsKeys.toilBank(toilEmployeeId) });
      queryClient.invalidateQueries({ queryKey: hrmsKeys.toilBank() });
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || 'TOIL consume failed');
    } finally {
      setConsuming(false);
    }
  };

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Overtime"
          subtitle="OT requests, approvals, and TOIL bank"
          primaryActions={
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/hr/overtime/new')}>
              New OT request
            </Button>
          }
        />
      }
    >
      <Tabs
        items={[
          {
            key: 'requests',
            label: 'OT requests',
            children: (
              <>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col xs={12} sm={6}>
                    <Card size="small">
                      <Statistic title="Total" value={stats.total} />
                    </Card>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Card size="small">
                      <Statistic title="Pending" value={stats.pending} valueStyle={{ color: '#1677ff' }} />
                    </Card>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Card size="small">
                      <Statistic title="Approved" value={stats.approved} valueStyle={{ color: '#52c41a' }} />
                    </Card>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Card size="small">
                      <Statistic title="Requested hrs" value={stats.hours} precision={1} />
                    </Card>
                  </Col>
                </Row>
                <Card size="small" style={{ marginBottom: 12 }}>
                  <Space>
                    <Select
                      allowClear
                      placeholder="Status"
                      style={{ width: 160 }}
                      value={statusFilter}
                      onChange={setStatusFilter}
                      options={Object.keys(HRMS_GENERIC_STATUS_COLOR).map((v) => ({ value: v, label: v }))}
                    />
                    <Text type="secondary">{filtered.length} record(s)</Text>
                  </Space>
                </Card>
                <Table
                  rowKey="id"
                  loading={isLoading}
                  columns={columns}
                  dataSource={filtered}
                  scroll={{ x: 900 }}
                  pagination={{ pageSize: 20 }}
                  onRow={(r) => ({
                    onClick: () => navigate(`/hr/overtime/${r.id}`),
                    style: { cursor: 'pointer' },
                  })}
                />
              </>
            ),
          },
          {
            key: 'toil',
            label: 'TOIL bank',
            children: (
              <>
                <Card size="small" style={{ marginBottom: 12 }}>
                  <Space wrap>
                    <Select
                      allowClear
                      placeholder="Filter employee"
                      style={{ width: 280 }}
                      options={empOptions}
                      value={toilEmployeeId}
                      onChange={setToilEmployeeId}
                      showSearch
                      optionFilterProp="label"
                    />
                    {toilEmployeeId && (
                      <Statistic title="Approx. balance" value={toilBalance} precision={2} suffix="hrs" />
                    )}
                    <Button
                      type="primary"
                      onClick={() => {
                        consumeForm.setFieldsValue({
                          employee_id: toilEmployeeId,
                          consume_date: dayjs(),
                        });
                        setConsumeOpen(true);
                      }}
                    >
                      Consume TOIL
                    </Button>
                  </Space>
                </Card>
                <Table
                  rowKey="id"
                  loading={toilLoading}
                  columns={toilColumns}
                  dataSource={toilRows}
                  pagination={{ pageSize: 20 }}
                  scroll={{ x: 800 }}
                />
              </>
            ),
          },
        ]}
      />

      <Modal
        title="Consume TOIL hours"
        open={consumeOpen}
        onCancel={() => setConsumeOpen(false)}
        onOk={consume}
        confirmLoading={consuming}
        destroyOnClose
      >
        <Form form={consumeForm} layout="vertical">
          <Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}>
            <Select options={empOptions} showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item name="hours" label="Hours to consume" rules={[{ required: true }]}>
            <InputNumber min={0.25} step={0.25} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="consume_date" label="Consume date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </WorkspaceLayout>
  );
};

export default OvertimePage;
